import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, PageHeader, Input, Modal, Alert } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const REASON_COLOR = { manual:'gray', unsubscribed:'warning', bounced:'danger', complained:'danger', competitor:'info' };

export default function Blacklist() {
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ email:'', reason:'manual' });
  const [saving, setSaving]     = useState(false);
  const fileRef = useRef();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    setLoading(true);
    try { const r = await api.get('/blacklist'); setList(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const addEmail = async () => {
    if (!form.email) return toast.error('Email required');
    setSaving(true);
    try { await api.post('/blacklist', form); toast.success('Added to blacklist'); setShowAdd(false); setForm({ email:'', reason:'manual' }); fetchList(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove from blacklist?')) return;
    try { await api.delete(`/blacklist/${id}`); fetchList(); toast.success('Removed'); } catch { toast.error('Failed'); }
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/blacklist/import', fd, { headers: { 'Content-Type':'multipart/form-data' } }); setImportResult(r.data); toast.success(`Added ${r.data.added} emails`); fetchList(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const exportCSV = async () => {
    try { const r = await api.get('/blacklist/export', { responseType:'blob' }); const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href=url; a.download='blacklist.csv'; a.click(); }
    catch { toast.error('Export failed'); }
  };

  const filtered = list.filter(b => !search || b.email.includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title="Blacklist / Suppression List" subtitle={`${list.length} suppressed emails — never emailed`} action={
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="secondary" size="sm" onClick={exportCSV}>⬇ Export</Button>
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>{importing ? '⏳' : '📂 Import CSV'}</Button>
          <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleCSV} />
          <Button onClick={() => setShowAdd(true)}>+ Add Email</Button>
        </div>
      } />

      <Alert type="info" style={{ marginBottom:20 }}>
        Emails on this list are <strong>automatically skipped</strong> by all campaigns. Unsubscribed leads are also added here automatically.
      </Alert>

      {importResult && (
        <Alert type="success" style={{ marginBottom:16 }}>
          CSV Import: <strong>{importResult.added}</strong> added from <strong>{importResult.total}</strong> emails.
        </Alert>
      )}

      <div style={{ marginBottom:16 }}>
        <input placeholder="🔍 Search by email..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ width:'100%', padding:'10px 14px', background:'#111', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:14, outline:'none' }} />
      </div>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        filtered.length === 0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🚫</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>Blacklist is empty</h3>
            <p style={{ color:'#555', fontSize:14, marginBottom:24 }}>Add emails you never want to contact. Unsubscribed leads are added automatically.</p>
            <Button onClick={() => setShowAdd(true)}>+ Add Email</Button>
          </Card>
        ) : (
          <Card style={{ padding:0, overflow:'hidden' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead><tr style={{ background:'rgba(46,204,113,0.05)', borderBottom:'1px solid rgba(46,204,113,0.1)' }}>
                  {['Email','Reason','Added','Remove'].map(h => <th key={h} style={{ padding:'11px 16px', textAlign:'left', color:'#555', fontWeight:700, fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {filtered.map(b => (
                    <tr key={b.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding:'10px 16px', color:'#ccc', fontFamily:'monospace' }}>{b.email}</td>
                      <td style={{ padding:'10px 16px' }}><Badge color={REASON_COLOR[b.reason]||'gray'}>{b.reason}</Badge></td>
                      <td style={{ padding:'10px 16px', color:'#555', fontSize:11 }}>{b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '—'}</td>
                      <td style={{ padding:'10px 16px' }}><Button size="sm" variant="danger" onClick={() => remove(b.id)}>✕</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add to Blacklist" size="sm">
        <Input label="Email Address *" type="email" placeholder="email@domain.com" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} />
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#ccc', marginBottom:6 }}>Reason</label>
          <select value={form.reason} onChange={e => setForm(p=>({...p,reason:e.target.value}))} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:14, outline:'none' }}>
            <option value="manual">Manual</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option>
            <option value="complained">Complained</option>
            <option value="competitor">Competitor</option>
          </select>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:8 }}>
          <Button variant="secondary" onClick={() => setShowAdd(false)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={saving} onClick={addEmail} style={{ flex:2 }}>Add to Blacklist</Button>
        </div>
      </Modal>
    </div>
  );
}
